import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { formatDistanceToNowStrict } from "date-fns";
import type { DuviSharedAdminStatusPillStatus } from "@duvi-agency/ui";
import { tickets as ticketsApi, type Ticket } from "../../api/client";
import { useDuviCoreAdminQuery } from "../../hooks/use-api";

const PER_PAGE = 5;

export const DUVI_CORE_ADMIN_TICKET_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "in-progress", label: "In Progress" },
  { value: "awaiting", label: "Awaiting" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];
export const DUVI_CORE_ADMIN_TICKET_PRIORITY_OPTIONS = [
  { value: "urgent", label: "Urgent" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];
export const DUVI_CORE_ADMIN_TICKET_CATEGORY_OPTIONS = [
  { value: "technical", label: "Technical" },
  { value: "billing", label: "Billing" },
  { value: "feature", label: "Feature Request" },
  { value: "question", label: "Question" },
];

export const DUVI_CORE_ADMIN_PRIORITY_COLOR: Record<string, string> = {
  urgent: "var(--error)",
  high: "var(--accent)",
  medium: "var(--info)",
  low: "var(--text-muted)",
};

export function duviCoreAdminPriorityColor(priority: string): string {
  return (
    DUVI_CORE_ADMIN_PRIORITY_COLOR[priority.toLowerCase()] ??
    "var(--text-muted)"
  );
}

export function duviCoreAdminPriorityLabel(priority: string): string {
  return priority
    ? priority.charAt(0).toUpperCase() + priority.slice(1)
    : priority;
}

function normaliseStatus(s: string): string {
  return (s || "").replace(/_/g, "-");
}

export function duviCoreAdminTicketStatusTone(
  status: string,
): DuviSharedAdminStatusPillStatus {
  switch (normaliseStatus(status)) {
    case "open":
      return "info";
    case "in-progress":
      return "warning";
    case "awaiting":
      return "info";
    case "resolved":
      return "success";
    case "closed":
      return "inactive";
    default:
      return "info";
  }
}

export function duviCoreAdminTicketStatusLabel(status: string): string {
  switch (normaliseStatus(status)) {
    case "open":
      return "Open";
    case "in-progress":
      return "In Progress";
    case "awaiting":
      return "Awaiting";
    case "resolved":
      return "Resolved";
    case "closed":
      return "Closed";
    default:
      return (status || "")
        .replace(/_/g, " ")
        .split(" ")
        .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
        .join(" ");
  }
}

export function duviCoreAdminRelativeTime(iso: string): string {
  if (!iso) return "—";
  try {
    return formatDistanceToNowStrict(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

export function useDuviCoreAdminSupportTicketsList() {
  const navigate = useNavigate();
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const join = (xs: string[]) => xs.filter(Boolean).join(",") || undefined;

  const { data, isLoading, meta } = useDuviCoreAdminQuery(
    () =>
      ticketsApi.list({
        status: join(selectedStatuses),
        priority: join(selectedPriorities),
        category: join(selectedCategories),
        search: searchQuery || undefined,
        limit: PER_PAGE,
      }),
    [
      selectedStatuses,
      selectedPriorities,
      selectedCategories,
      searchQuery,
      currentPage,
    ],
  );

  const tickets: Ticket[] = data ?? [];
  const totalCount = meta?.total_count ?? tickets.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PER_PAGE));

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatuses, selectedPriorities, selectedCategories, searchQuery]);

  // The export shows an SLA-warning banner; the platform API does not expose an
  // SLA field yet, so this stays 0 (banner hidden) — as the prior build did.
  const slaWarningCount = 0;

  return {
    tickets,
    isLoading,
    perPage: PER_PAGE,
    totalCount,
    totalPages,
    currentPage,
    setCurrentPage,
    selectedStatuses,
    setSelectedStatuses,
    selectedPriorities,
    setSelectedPriorities,
    selectedCategories,
    setSelectedCategories,
    searchQuery,
    setSearchQuery,
    slaWarningCount,
    openTicket: (id: string) => navigate(`/support/tickets/${id}`),
  };
}
